import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isAcceptedAsyncSubmissionStatus,
  SubmissionLifecycleState as SubmissionLifecycleStateType,
  SubmissionStatusRecord as SubmissionStatusRecordType,
} from '../submissionState';
import {
  getSubmissionPollResolution,
  getSubmissionStatusPayload,
  invalidateSubmissionStatus,
  receiveSubmissionStatus,
} from '../../store/ducks/submission';
import { submissionPolling } from '../../store/submissionPolling';
import { State } from '../../store/ducks';

export type SubmissionLifecycleState = SubmissionLifecycleStateType;
export type SubmissionStatusRecord = SubmissionStatusRecordType;

export type SubmitAcceptedResponse = {
  submissionId?: string | null;
  status?: string | null;
};

export type SubmissionValidationResult = {
  valid: boolean;
  blockingMessage?: string;
  // How the blocking message is shown (a NOTIFY_TYPE); callers show an error when it is absent.
  blockingNotifyType?: string;
};

export type SubmitErrorClassification = {
  message: string;
  canRetry: boolean;
};

const TERMINAL_STATUS = {
  SUCCEEDED: 'succeeded',
  REJECTED: 'rejected',
  ERROR: 'error',
} as const;

export type UseSubmissionProgressOptions = {
  caseExternalId: string | null | undefined;
  validate: () => Promise<SubmissionValidationResult>;
  submit: () => Promise<SubmitAcceptedResponse>;
  classifySubmitError: (error: unknown) => SubmitErrorClassification;
  isSubmitInFlightError?: (error: unknown) => boolean;
  onBlocked?: () => void;
  onValidationBlocked?: (message?: string, notifyType?: string) => void;
  onUnexpectedSubmitStatus?: () => void;
  onSubmitErrorClassified?: (classification: SubmitErrorClassification, error: unknown) => void;
  onTerminalSucceeded?: (statusRecord: SubmissionStatusRecord) => void;
  onTerminalRejected?: (statusRecord: SubmissionStatusRecord) => void;
  onTerminalError?: (statusRecord: SubmissionStatusRecord) => void;
};

export type UseSubmissionProgressResult = {
  submissionState: SubmissionLifecycleState;
  isSubmissionPending: boolean;
  currentSubmissionId: string | null;
  isSubmitBlocked: () => boolean;
  attemptSubmit: (attemptOptions?: { blockedByCaller?: boolean }) => Promise<void>;
  restoreInFlightSubmission: (submissionId: string) => void;
};

/**
 * Coordinates submit callbacks with the Redux-backed polling lifecycle. Redux owns backend
 * snapshots while this hook retains only immediate validation and mutation failures.
 */
const useSubmissionProgress = (
  options: UseSubmissionProgressOptions
): UseSubmissionProgressResult => {
  const {
    caseExternalId,
    validate,
    submit,
    classifySubmitError,
    isSubmitInFlightError,
    onBlocked,
    onValidationBlocked,
    onUnexpectedSubmitStatus,
    onSubmitErrorClassified,
    onTerminalSucceeded,
    onTerminalRejected,
    onTerminalError,
  } = options;

  const dispatch = useDispatch();
  const statusRecord = useSelector((state: State) =>
    getSubmissionStatusPayload(state, caseExternalId)
  );
  const pollResolution = useSelector((state: State) =>
    getSubmissionPollResolution(state, caseExternalId)
  ) || {
    nextState: 'idle' as SubmissionLifecycleState,
    shouldContinuePolling: false,
  };
  const [localSubmissionState, setLocalSubmissionState] = useState<SubmissionLifecycleState | null>(
    null
  );
  const submitInFlightRef = useRef(false);
  const retryBlockedRef = useRef(false);
  const lastTerminalNotificationRef = useRef<string | null>(null);

  const submissionState = localSubmissionState || pollResolution.nextState;
  const currentSubmissionId = pollResolution.shouldContinuePolling
    ? statusRecord?.submissionId || null
    : null;

  const isSubmitBlocked = useCallback(
    (): boolean =>
      submitInFlightRef.current || retryBlockedRef.current || submissionState === 'pending',
    [submissionState]
  );

  // A status check this app gave up on is tried again when a screen opens the record.
  useEffect(() => {
    if (caseExternalId) {
      submissionPolling.resumeAfterGiveUp(caseExternalId);
    }
  }, [caseExternalId]);

  // Without a submission id to poll, follow whatever the backend has in flight for the record, so
  // the screen does not read as pending for the rest of the visit.
  const followSubmissionInFlight = useCallback((): void => {
    if (caseExternalId) {
      submissionPolling.followSubmissionByCase(caseExternalId);
    }
  }, [caseExternalId]);

  useEffect(() => {
    if (
      !statusRecord ||
      pollResolution.shouldContinuePolling ||
      pollResolution.nextState === 'idle'
    ) {
      return;
    }

    const terminalNotificationKey = `${statusRecord.submissionId || 'no-submission'}::${
      statusRecord.status
    }`;
    if (lastTerminalNotificationRef.current === terminalNotificationKey) {
      return;
    }
    lastTerminalNotificationRef.current = terminalNotificationKey;

    if (statusRecord.status === TERMINAL_STATUS.SUCCEEDED) {
      onTerminalSucceeded?.(statusRecord);
    } else if (statusRecord.status === TERMINAL_STATUS.REJECTED) {
      onTerminalRejected?.(statusRecord);
    } else if (statusRecord.status === TERMINAL_STATUS.ERROR) {
      onTerminalError?.(statusRecord);
    } else {
      return;
    }

    // The terminal callback has now been delivered — the toast/banner it triggers
    // is this hook's job, not the store's. Leaving the terminal snapshot behind
    // means a remount (nav away and back, without a reload) gets a fresh
    // lastTerminalNotificationRef and replays the same callback, showing the same
    // toast again for a submission that finished before the remount. Invalidating
    // clears it so a remount reads nothing rather than stale terminal state.
    if (caseExternalId) {
      dispatch(invalidateSubmissionStatus(caseExternalId));
    }
  }, [
    statusRecord,
    pollResolution,
    caseExternalId,
    dispatch,
    onTerminalSucceeded,
    onTerminalRejected,
    onTerminalError,
  ]);

  const attemptSubmit = useCallback(
    async (attemptOptions?: { blockedByCaller?: boolean }): Promise<void> => {
      if (attemptOptions?.blockedByCaller || isSubmitBlocked()) {
        onBlocked?.();
        return;
      }

      submitInFlightRef.current = true;
      setLocalSubmissionState(null);

      try {
        const validationResult = await validate();

        if (!validationResult.valid) {
          onValidationBlocked?.(
            validationResult.blockingMessage,
            validationResult.blockingNotifyType
          );
          setLocalSubmissionState('error');
          return;
        }

        const submitResponse = await submit();
        const nextSubmissionId = submitResponse?.submissionId || null;
        const status = submitResponse?.status;

        if (isAcceptedAsyncSubmissionStatus(status)) {
          if (caseExternalId && nextSubmissionId) {
            const pollGeneration = submissionPolling.restartPolling(
              caseExternalId,
              nextSubmissionId,
              0,
              true
            );
            if (pollGeneration !== null) {
              dispatch(
                receiveSubmissionStatus(
                  caseExternalId,
                  { submissionId: nextSubmissionId, status },
                  pollGeneration
                )
              );
            }
          } else {
            followSubmissionInFlight();
          }
          return;
        }

        onUnexpectedSubmitStatus?.();
        retryBlockedRef.current = true;
        setLocalSubmissionState('error');
      } catch (error) {
        if (isSubmitInFlightError?.(error)) {
          onBlocked?.();
          followSubmissionInFlight();
          return;
        }

        const classification = classifySubmitError(error);
        onSubmitErrorClassified?.(classification, error);
        if (!classification.canRetry) {
          retryBlockedRef.current = true;
        }
        setLocalSubmissionState('error');
      } finally {
        submitInFlightRef.current = false;
      }
    },
    [
      isSubmitBlocked,
      validate,
      submit,
      isSubmitInFlightError,
      caseExternalId,
      dispatch,
      followSubmissionInFlight,
      classifySubmitError,
      onBlocked,
      onValidationBlocked,
      onUnexpectedSubmitStatus,
      onSubmitErrorClassified,
    ]
  );

  const restoreInFlightSubmission = useCallback(
    (submissionId: string): void => {
      if (!caseExternalId || !submissionId || submissionPolling.isPolling(caseExternalId)) {
        return;
      }

      const pollGeneration = submissionPolling.restartPolling(caseExternalId, submissionId);
      if (pollGeneration !== null) {
        dispatch(
          receiveSubmissionStatus(
            caseExternalId,
            { submissionId, status: 'processing' },
            pollGeneration
          )
        );
      }
    },
    [caseExternalId, dispatch]
  );

  return {
    submissionState,
    isSubmissionPending: submissionState === 'pending',
    currentSubmissionId,
    isSubmitBlocked,
    attemptSubmit,
    restoreInFlightSubmission,
  };
};

export default useSubmissionProgress;
